'use client';

import { useKeepScrollOnResize } from './use-keep-scroll-on-resize';
import { AnswerModel } from './answer-model';
import { FollowedSkills } from '@/workspace/followed-skills';
import { UserMessageAttachments } from '@/components/assistant-ui/attachment';
import { Composer as ThreadComposer } from '@/components/assistant-ui/composer';
import { usePhoneComposerLayout } from '@/components/assistant-ui/composer-layout';
import { ThreadFollowupSuggestions } from '@/components/assistant-ui/follow-up-suggestions';
import { MarkdownText } from '@/components/assistant-ui/markdown-text';
import { Reasoning } from '@/components/assistant-ui/reasoning';
import {
  ToolFallback,
  LegacyVoiceReceipt,
  isLegacyVoiceReceipt,
} from '@/components/assistant-ui/tool-fallback';
import { ToolActivity } from '@/components/assistant-ui/tool-activity';
import { MessageActivity } from '@/components/assistant-ui/message-activity';
import {
  BuildStatusLine,
  BuildStepGroup,
  BuildSuggestionChips,
  isBuildMessage,
} from '@/components/assistant-ui/build-experience';
import { PendingApprovalNotice } from '@/components/assistant-ui/pending-approvals';
import {
  ActionConfirmationCard,
  ActionConfirmationReference,
  isActionPointer,
} from '@/components/assistant-ui/action-confirmation';
import { CONNECTOR_ACTION_TOOLS, ConnectorApprovalGroup } from './connector-approval-group';
import { TooltipIconButton } from '@/components/assistant-ui/tooltip-icon-button';
import { Button } from '@/components/ui/button';
import { touchControlVariants } from '@/components/ui/touch-control';
import { cn } from '@/lib/utils';
import { shouldAutoFocusComposer } from '@/lib/mobile';
import { useChatCopy } from '@/lib/chat-i18n';
import { useNamespaceCopy } from '@/i18n/ns';
import chatUnconfirmedCopy from '@/i18n/ns/chat-unconfirmed';
import {
  clearUnconfirmed,
  markUnconfirmed,
  readUnconfirmed,
  subscribeUnconfirmed,
  useChatCheckAgain,
  type UnconfirmedTurn,
} from '@/lib/chat-check-again';
import { truncatedByLength } from '@/lib/truncation';
import { SpeechNotice } from '@/speech/ui/speech-notice';
import { PendingVoiceBubble } from '@/voice/pending-bubble';
import { ReadAloudAction } from '@/voice/playback/read-aloud-action';
import { InterruptedReply } from '@/voice/interrupted-reply';
import { splitAtSteers, steerReadPart, steerUnreadPart } from '@/lib/steer/parts';
import { AnswerProgressNotice } from './answer-progress';
import { CompactionStatus, useCompactionActive } from './compaction-status';
import { SteerRead } from './steer-read';
import { WaitingMessages } from './waiting-messages';
import {
  ActionBarMorePrimitive,
  ActionBarPrimitive,
  AuiIf,
  type AssistantState,
  BranchPickerPrimitive,
  ComposerPrimitive,
  ErrorPrimitive,
  groupPartByType,
  getExternalStoreMessages,
  MessagePrimitive,
  SuggestionPrimitive,
  ThreadPrimitive,
  type ToolCallMessagePartComponent,
  useAuiState,
  useMessageRuntime,
} from '@assistant-ui/react';
import {
  AlertCircleIcon,
  ArrowDownIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CopyIcon,
  DownloadIcon,
  MoreHorizontalIcon,
  PencilIcon,
  RefreshCwIcon,
} from 'lucide-react';
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ComponentType,
  type FC,
  type PropsWithChildren,
} from 'react';
import { useQuestionGate, useBillingEditDraftGuard } from '@/billing/context';
import { useMessageRepair } from '@/lib/message-repair-context';
import { MessageRepairComposer } from './message-repair-composer';
import { chatErrorMessage, editingAppRefusal } from '@/lib/chat-error-message';
import { localizeWorkspaceError } from '@/lib/workspace-error-copy';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { chatErrorCopy } from '@/i18n/ns/chat-error';
import { useMessageError } from '@assistant-ui/core/react';
import { useCompany } from '@/company/context';
import { useOptionalWorkspace } from '@/workspace/context';
import { pendingActionScope, pendingActionStore } from './pending-action-store';
import { actionPresentation } from './action-layout';
import { Spark, WakingSpark } from '@/components/spark';
import { ASK_USER_QUESTION } from '@/workspace/question-contract';
import { STORE_READ_TOOLS } from '@/workspace/storefront/read-request';
import { RENDER_TOOL_NAMES } from '@/render-kit/registry';
import { RenderToolPart } from '@/render-kit/render-tool-part';

export type ThreadGroupPart = MessagePrimitive.GroupedParts.GroupPart;

// Tool evidence shares one activity disclosure.
// A tool whose UI IS the assistant's visible answer must not: ask_user_question
// renders the question the user has to answer, so it stays in message order
// outside the group, the count never includes it, and a turn whose only call is
// the question shows no group row at all. Keyed on the tool NAME, never on text.
// Cards the user acts on stay visible instead of collapsing into the tool group.
const STANDALONE_TOOL_NAMES: ReadonlySet<string> = new Set([
  ASK_USER_QUESTION,
  'offer_connections',
  'propose_knowledge_change',
  'suggest_page_edits',
  'read_external_mcp',
  // Settings changed by asking: the line says what changed and offers Undo.
  'update_my_settings',
  'create_skill_draft',
  'update_skill_draft',
  'save_work_result',
  // The native build approval is a card the person acts on: it stays visible.
  'build_artifact',
  'create_agent',
  'update_agent',
  'set_agent_trigger',
  'run_agent',
  'propose_company_knowledge',
  // A store's design saved like an App: its card opens the storefront canvas.
  'change_storefront_design',
  // A paid read of the shop, or a check of its saved work, is a request card the person approves,
  // not a step in the trace.
  ...STORE_READ_TOOLS,
  // Projects v1: cards the person reads or acts on (Create, Move).
  'draft_project',
  'suggest_project_move',
  'move_chat_to_project',
  'add_to_project',
  'remove_from_project',
  // An App's secret: a secure field the owner types into, never part of the trace.
  'request_app_secret',
  // Render Kit cards (email, conversation, draft) are what the person reads or sends.
  ...RENDER_TOOL_NAMES,
]);

// groupPartByType tags its result with a registered memo-key symbol so
// GroupedParts can memoize the tree across renders. Wrapping it would drop that,
// so the wrapper is built once and carries its own stable fingerprint.
const GROUP_BY_MEMO_KEY = Symbol.for('@assistant-ui/groupBy.memoKey');
const groupAssistantParts = (() => {
  const base = groupPartByType({
    reasoning: ['group-chainOfThought', 'group-reasoning'],
    'tool-call': ['group-chainOfThought', 'group-tool'],
    'standalone-tool-call': [],
  });
  const grouped: typeof base = (part, context) =>
    part.type === 'tool-call' &&
    (STANDALONE_TOOL_NAMES.has(part.toolName) ||
      isActionPointer(part.result) ||
      isLegacyVoiceReceipt(part.result))
      ? []
      : base(part, context);
  return Object.assign(grouped, {
    [GROUP_BY_MEMO_KEY]: `${(base as unknown as Record<symbol, string>)[GROUP_BY_MEMO_KEY]}+standalone:${[...STANDALONE_TOOL_NAMES].sort().join(',')}`,
  });
})();

/**
 * Optional component overrides for the thread. `AssistantMessage` and
 * `Welcome` replace whole sections; the remaining slots override how the
 * assistant message renders tool calls and part groups. Tool UIs registered
 * by name (toolkit `render`, `useAssistantDataUI`) take precedence over
 * `ToolFallback`.
 */
export type ThreadComponents = {
  AssistantMessage?: ComponentType | undefined;
  Welcome?: ComponentType | undefined;
  SuggestionContent?: ComponentType | undefined;
  SuggestionsHeader?: ComponentType | undefined;
  ToolFallback?: ToolCallMessagePartComponent | undefined;
  ToolGroup?: ComponentType<PropsWithChildren<{ group: ThreadGroupPart }>> | undefined;
  /** Docked to the top edge of the composer, inside the viewport footer: for a
   * control that belongs WITH the composer rather than in the transcript. */
  ComposerDock?: ComponentType | undefined;
  /** After the composer and the starter suggestions, on an empty thread only:
   * for what belongs on the start screen below the question (the company feed). */
  WelcomeFooter?: ComponentType | undefined;
  ReasoningGroup?: ComponentType<PropsWithChildren<{ group: ThreadGroupPart }>> | undefined;
  /** Replaces the composer: a reader of someone else's shared chat gets a
   * read-only bar instead (collaboration v1). */
  Composer?: ComponentType | undefined;
  /** Rendered above each user message's text (e.g. "Suggested by Erik"). */
  UserMessageHeader?: ComponentType | undefined;
  /** Wraps each user message and may show something else in its place: a run chat shows its
   * run card instead of the agent's internal task message (ui/workspace/agents/run-card.tsx). */
  UserMessageWrapper?: ComponentType<PropsWithChildren> | undefined;
  /** Reading someone else's chat: nothing here may start or change a turn —
   * no Try again, Edit, follow-up suggestions, continue or composer dock. */
  readOnly?: boolean | undefined;
  /** Rendered after the last message, e.g. "Anna is getting an answer". */
  AfterMessages?: ComponentType | undefined;
  /** Extra items in each answer's More menu (e.g. "Fork from here"). */
  MessageMenuItems?: ComponentType | undefined;
  /** Under a collapsed "N tool calls" row: a note about those calls (e.g.
   * "From Anna's Gmail"), visible without opening the row. */
  ToolGroupNote?: ComponentType<{ toolCallIds: string[] }> | undefined;
  /** While an answer streams below the fold, the scroll button reads "New
   * answer ↓" instead of showing only an arrow (a follower of the chat). */
  newAnswerPill?: boolean | undefined;
  /** Open this mount at this scroll offset instead of the bottom: a follower
   * who had scrolled up keeps their place when the pane re-attaches. */
  keepScrollTop?: number | null | undefined;
};

export type ThreadProps = {
  components?: ThreadComponents | undefined;
};

const EMPTY_COMPONENTS: ThreadComponents = {};

/** Exported for contract tests that mount a message's parts without the thread. */
export const ThreadComponentsContext = createContext<ThreadComponents>(EMPTY_COMPONENTS);

// Startup exposes a loading placeholder thread; treat it as a new chat so
// the composer mounts centered. Loads after startup keep the docked layout.
const isNewChatView = (s: AssistantState) =>
  s.thread.messages.length === 0 && (!s.thread.isLoading || s.threads.isLoading);

export const Thread: FC<ThreadProps> = ({ components = EMPTY_COMPONENTS }) => {
  const isEmpty = useAuiState(isNewChatView);

  return (
    <ThreadComponentsContext.Provider value={components}>
      <ThreadRoot isEmpty={isEmpty} />
    </ThreadComponentsContext.Provider>
  );
};

const ThreadRoot: FC<{ isEmpty: boolean }> = ({ isEmpty }) => {
  // A reader of someone else's shared chat gets the read-only bar here.
  const {
    Welcome = ThreadWelcome,
    ComposerDock,
    Composer = ThreadComposer,
    readOnly = false,
    AfterMessages,
    keepScrollTop = null,
    WelcomeFooter,
    SuggestionsHeader,
  } = useContext(ThreadComponentsContext);
  const phoneComposerLayout = usePhoneComposerLayout();
  // A home launchpad can be taller than a phone viewport. Keep its composer
  // and cards in document flow, instead of making the whole deck sticky.
  const homeLaunchpad = Boolean(SuggestionsHeader);
  const suggestionsAboveComposer = phoneComposerLayout && !homeLaunchpad;
  const viewport = useRef<HTMLDivElement>(null);
  const keep = keepScrollTop !== null;
  useKeepScrollOnResize(viewport);
  useLayoutEffect(() => {
    if (keep && viewport.current) viewport.current.scrollTop = keepScrollTop;
  }, [keep, keepScrollTop]);

  return (
    <ThreadPrimitive.Root
      className="aui-root aui-thread-root bg-background @container flex h-full flex-col"
      style={{
        ['--thread-max-width' as string]: '60rem',
        ['--composer-bg' as string]: 'var(--color-card)',
        ['--composer-radius' as string]: '1.5rem',
        ['--composer-padding' as string]: '8px',
      }}
    >
      <ThreadPrimitive.Viewport
        ref={viewport}
        turnAnchor="top"
        scrollToBottomOnInitialize={!keep}
        scrollToBottomOnRunStart={!keep}
        data-slot="aui_thread-viewport"
        className="relative flex flex-1 flex-col overflow-x-hidden overflow-y-scroll scroll-smooth"
      >
        <div
          className={cn(
            'mx-auto flex w-full max-w-(--thread-max-width) flex-1 flex-col px-4 pt-4',
            isEmpty && 'md:justify-center',
          )}
        >
          <AuiIf condition={isNewChatView}>
            <Welcome />
          </AuiIf>

          <div data-slot="aui_message-group" className="mb-14 flex flex-col gap-y-6 empty:hidden">
            <ThreadPrimitive.Messages>{() => <ThreadMessage />}</ThreadPrimitive.Messages>
            <DetachedRunningIndicator />
            <PendingVoiceBubble />
            <WaitingMessages />
            {AfterMessages && <AfterMessages />}
          </div>

          <ThreadPrimitive.ViewportFooter
            className={cn(
              'aui-thread-viewport-footer bg-background sticky bottom-0 z-10 mt-auto flex flex-col gap-4 overflow-visible pb-2 md:pb-6',
              isEmpty && (homeLaunchpad ? 'static mt-0' : 'md:static md:mt-0'),
              !isEmpty && 'rounded-t-(--composer-radius)',
            )}
          >
            <ThreadScrollToBottom />
            {!readOnly && <ThreadFollowupSuggestions />}
            {!readOnly && <BuildSuggestionChips />}
            <BuildStatusLine />
            <UnconfirmedNotice />
            <SpeechNotice />
            {suggestionsAboveComposer && (
              <AuiIf condition={isNewChatView}>
                <ThreadSuggestionsPresence />
              </AuiIf>
            )}
            {ComposerDock && !readOnly && <ComposerDock />}
            <PendingApprovalNotice />
            <Composer />
            {!suggestionsAboveComposer && (
              <AuiIf condition={isNewChatView}>
                <ThreadSuggestionsPresence />
              </AuiIf>
            )}
          </ThreadPrimitive.ViewportFooter>
          {WelcomeFooter && (
            <AuiIf condition={isNewChatView}>
              <WelcomeFooter />
            </AuiIf>
          )}
        </div>
      </ThreadPrimitive.Viewport>
    </ThreadPrimitive.Root>
  );
};

const ThreadMessage: FC = () => {
  const { AssistantMessage: AssistantMessageComponent = AssistantMessage } =
    useContext(ThreadComponentsContext);
  const role = useAuiState((s) => s.message.role);
  const isEditing = useAuiState((s) => s.message.composer.isEditing);

  if (isEditing) return <EditComposer />;
  if (role === 'user') return <UserMessage />;
  return <AssistantMessageComponent />;
};

// A part is "visible" only if AssistantMessage actually renders it (see its
// GroupedParts switch): text, reasoning, and tool calls. Everything else —
// `data` (e.g. find_right_tools' persisted pack attachment) and any
// source/file/image part — falls through that switch to `null`, so it is not an
// activity signal and must NOT dismiss "Tänker…" while the screen is still
// blank. Intelligence-enhancing tools are stripped from the stream entirely
// (src/stream-filter.mjs), so during that phase the assistant message has no
// visible part at all.
const isVisibleAssistantPart = (part: { type: string; text?: string }): boolean => {
  switch (part.type) {
    case 'text':
      return Boolean(part.text && part.text.trim());
    case 'reasoning':
    case 'tool-call':
      return true;
    default:
      return false;
  }
};

// The detached fallback exists only for the submitted-before-stream gap, before
// assistant-ui has mounted an assistant message. Once that message exists, the
// synthetic `indicator` part is rendered inside <AssistantMessage/>, in the
// same place as reasoning and tool groups.
const useShowDetachedThinking = (): boolean =>
  useAuiState((s) => {
    if (!s.thread.isRunning) return false;
    const last = s.thread.messages[s.thread.messages.length - 1];
    if (!last || last.role !== 'assistant') return true;
    return false;
  });

// Show the message-scoped "thinking" affordance while a running assistant
// message has produced no visible part yet. Hidden data parts may be present
// during internal tool selection, but visible text/reasoning/tool calls should
// own the row as soon as they arrive.
const useShowAssistantMessageThinking = (): boolean =>
  useAuiState((s) => {
    if (s.message.role !== 'assistant' || s.message.status?.type !== 'running') return false;
    return !s.message.content.some(isVisibleAssistantPart);
  });

// The wait before the first visible part reads as the same calm activity line:
// the brand spark working beside a static "Thinking…" (ACTIONS-DESIGN §3.2).
// Reduced motion stills the spark; the label never animates.
const ThinkingIndicator: FC = () => {
  const copy = useChatCopy();
  return (
    <div
      data-slot="aui_thinking-indicator"
      className="pc-thinking-line"
      aria-live="polite"
      aria-label={copy.thinking}
    >
      <Spark
        size={14}
        state="thinking"
        withRays={false}
        body="currentColor"
        hole="var(--pc-paper)"
      />
      <span>{copy.activityThinking}</span>
    </div>
  );
};

const AssistantMessageThinkingIndicator: FC = () => {
  const show = useShowAssistantMessageThinking();
  const compacting = useCompactionActive();
  return show && !compacting ? <ThinkingIndicator /> : null;
};

const DetachedRunningIndicator: FC = () => {
  const show = useShowDetachedThinking();
  return show ? <ThinkingIndicator /> : null;
};

const ThreadScrollToBottom: FC = () => {
  const copy = useChatCopy();
  const { newAnswerPill } = useContext(ThreadComponentsContext);
  const running = useAuiState((s) => s.thread.isRunning);
  if (newAnswerPill && running)
    return (
      <ThreadPrimitive.ScrollToBottom asChild>
        <button
          type="button"
          className="aui-thread-new-answer bg-background text-foreground absolute -top-12 z-10 flex items-center gap-1.5 self-center rounded-full border px-3.5 py-1.5 text-sm font-medium shadow-(--shadow-soft) disabled:invisible"
        >
          {copy.newAnswer}
          <ArrowDownIcon className="size-4" />
        </button>
      </ThreadPrimitive.ScrollToBottom>
    );
  return (
    <ThreadPrimitive.ScrollToBottom asChild>
      <TooltipIconButton
        tooltip={copy.scrollToBottom}
        variant="outline"
        className="aui-thread-scroll-to-bottom dark:border-border dark:bg-background dark:hover:bg-accent absolute -top-12 z-10 self-center rounded-full p-4 shadow-(--shadow-soft) disabled:invisible"
      >
        <ArrowDownIcon />
      </TooltipIconButton>
    </ThreadPrimitive.ScrollToBottom>
  );
};

// The same greeting spark the auth door and sidebar use (ui/components/spark.tsx).
const WelcomeSpark: FC = () => <WakingSpark size={36} className="relative" />;

const ThreadWelcome: FC = () => {
  const copy = useChatCopy();
  return (
    <div className="aui-thread-welcome-root relative mt-auto mb-8 flex flex-col items-center gap-5 px-4 text-center md:mt-0">
      {/* The spark's wash sits behind the greeting so the empty state carries
          the brand without a hero image or extra copy. */}
      <div aria-hidden className="brand-wash -top-24 left-1/2 size-72 -translate-x-1/2" />
      <WelcomeSpark />
      <h1 className="aui-thread-welcome-message-inner fade-in slide-in-from-bottom-2 animate-in fill-mode-both relative text-[1.75rem] leading-[1.15] font-semibold tracking-[-0.025em] text-balance duration-500 ease-(--ease-out-quart) md:text-[2.25rem]">
        {copy.welcomeHeading}
      </h1>
    </div>
  );
};

const ThreadSuggestionsPresence: FC = () => {
  const isComposerEmpty = useAuiState((s) => s.composer.isEmpty);

  return (
    <div
      aria-hidden={!isComposerEmpty}
      inert={!isComposerEmpty}
      className={cn(
        'aui-thread-welcome-suggestions-presence grid w-full transition-[grid-template-rows,opacity] duration-200 motion-reduce:transition-none',
        isComposerEmpty
          ? 'grid-rows-[1fr] opacity-100'
          : 'pointer-events-none grid-rows-[0fr] opacity-0',
      )}
    >
      <div className="min-h-0 overflow-hidden">
        <ThreadSuggestions />
      </div>
    </div>
  );
};

const ThreadSuggestions: FC = () => {
  const { SuggestionsHeader } = useContext(ThreadComponentsContext);
  return (
    <>
      {SuggestionsHeader && <SuggestionsHeader />}
      <div className="aui-thread-welcome-suggestions grid w-full min-w-0 grid-cols-1 gap-2 px-0 md:flex md:flex-wrap md:items-center md:justify-center">
        <ThreadPrimitive.Suggestions>{() => <ThreadSuggestionItem />}</ThreadPrimitive.Suggestions>
      </div>
    </>
  );
};

const ThreadSuggestionItem: FC = () => {
  const { needsAuth, requestAuth } = useQuestionGate();
  const { SuggestionContent = ThreadSuggestionContent } = useContext(ThreadComponentsContext);
  // Suggestion clicks are a third send path: `Trigger send` calls
  // thread.append() directly, bypassing the composer's onSubmit/button gates.
  // When auth is required and signed-out, drop `send` (so the trigger only
  // fills the composer with the prompt) and open the sign-in view instead —
  // the armed pending send then fires the suggestion right after sign-in.
  return (
    <div className="aui-thread-welcome-suggestion-display fade-in slide-in-from-bottom-2 animate-in min-w-0 fill-mode-both duration-200 md:max-w-80">
      <SuggestionPrimitive.Trigger
        asChild
        {...(needsAuth ? { onClick: requestAuth } : { send: true })}
      >
        <Button
          variant="ghost"
          className="aui-thread-welcome-suggestion text-foreground bg-card hover:bg-accent hover:border-control-border/60 border-border/70 h-auto min-h-11 w-full min-w-0 max-w-full flex-wrap justify-start gap-x-1.5 gap-y-0 rounded-xl border px-4 py-2.5 text-left text-sm font-normal whitespace-normal break-words shadow-[0_1px_2px_oklch(0_0_0/0.03)] transition-[color,background-color,border-color,box-shadow,transform] duration-200 ease-(--ease-out-quart) md:min-h-9 md:justify-center md:rounded-full md:px-4 md:py-1.5 md:text-center pointer-coarse:min-h-11"
        >
          <SuggestionContent />
        </Button>
      </SuggestionPrimitive.Trigger>
    </div>
  );
};

const ThreadSuggestionContent: FC = () => (
  <>
    <SuggestionPrimitive.Title className="aui-thread-welcome-suggestion-text-1 text-muted-foreground min-w-0 max-w-full whitespace-normal break-words font-medium md:text-foreground md:font-normal" />
    <SuggestionPrimitive.Description className="aui-thread-welcome-suggestion-text-2 min-w-0 max-w-full whitespace-normal break-words empty:hidden" />
  </>
);

/** Answers that exist: assistant messages that did not end in an error. */
const answeredCount = (
  messages: readonly { role: string; status?: { type?: string; reason?: string } }[],
) =>
  messages.filter(
    (message) =>
      message.role === 'assistant' &&
      !(message.status?.type === 'incomplete' && message.status.reason === 'error'),
  ).length;

/** The unknown-outcome record of this chat (kept across the Check again remount; see chat-check-again). */
function useUnconfirmedTurn(): UnconfirmedTurn | null {
  const ownerId = useChatCheckAgain()?.ownerId;
  const threadId = useOptionalWorkspace()?.threadId;
  const turn = useSyncExternalStore(
    subscribeUnconfirmed,
    () => JSON.stringify(readUnconfirmed(ownerId, threadId)),
    () => 'null',
  );
  return JSON.parse(turn);
}

/** The one visible unknown-outcome notice of a chat; it survives the Check again remount. */
export const UnconfirmedNotice: FC = () => {
  const threadId = useOptionalWorkspace()?.threadId;
  const recovery = useChatCheckAgain();
  const ownerId = recovery?.ownerId;
  const checkAgain = recovery?.checkAgain;
  const copy = useNamespaceCopy(chatUnconfirmedCopy);
  const marked = useUnconfirmedTurn();
  const messages = useAuiState((s) => s.thread.messages);
  const last = messages.at(-1);
  const assistants = answeredCount(messages);
  // The transport marks the original before any answer count exists; this pane counts the answers it sees.
  const counted = marked !== null && marked.assistantCount !== null;
  // An answer arrived, or the person sent something else: the old question is settled or superseded.
  const settled =
    marked !== null &&
    counted &&
    ((marked.assistantCount ?? 0) < assistants ||
      (last?.role === 'user' && last.id !== marked.messageId));
  useEffect(() => {
    if (!ownerId || !threadId || !marked) return;
    if (settled) clearUnconfirmed(ownerId, threadId);
    else if (!counted)
      markUnconfirmed(ownerId, threadId, { ...marked, assistantCount: assistants });
  }, [ownerId, threadId, marked, settled, counted, assistants]);
  if (!checkAgain || !marked || settled) return null;
  return (
    <div
      className="aui-message-error-root border-border/70 bg-muted/40 text-foreground mx-auto mt-2 flex w-full max-w-(--thread-max-width) flex-wrap items-center gap-x-2.5 gap-y-2 rounded-xl border p-3 text-sm"
      role="status"
      data-slot="unconfirmed-submission"
    >
      <AlertCircleIcon aria-hidden className="text-muted-foreground size-4 shrink-0" />
      <span className="min-w-0 flex-1 text-pretty">{copy.chatUnconfirmed}</span>
      <button
        type="button"
        className="bg-background hover:bg-muted/80 text-foreground shrink-0 rounded-full border px-3 py-1 text-sm transition-colors ease-in"
        onClick={checkAgain}
      >
        {copy.chatCheckAgain}
      </button>
    </div>
  );
};

export const MessageError: FC = () => {
  const errorCopy = useNamespaceCopy(chatErrorCopy);
  const locale = useChatInterfaceLocale();
  const error = useMessageError();
  const marked = useUnconfirmedTurn();
  const checkAgain = useChatCheckAgain()?.checkAgain;
  const ws = useOptionalWorkspace();
  const messages = useAuiState((s) => s.thread.messages);
  const original = [...messages].reverse().find((message) => message.role === 'user');
  const editing = editingAppRefusal(error);
  const gate = useQuestionGate();
  const turn = useMessageRuntime();
  // Continue without the preview: once the App has closed (this render shows no canvas), the same message is sent
  // again with no App selected; continuing saved work requires Chat to find and open that App.
  const [continuing, setContinuing] = useState(false);
  const canvasClosed = ws?.canvas === null;
  // Only this message's refusal, and only while the preview on show is that same gone preview: another App opened
  // since (a saved editor included) is never closed or replaced by a fresh one from here.
  const refusalHolds = (closed = false) =>
    Boolean(ws && original && ws.goneRefusalHolds(ws.threadId, original.id, closed));
  const recoverable = editing === 'gone' && refusalHolds();
  useEffect(() => {
    if (!continuing || !canvasClosed) return;
    setContinuing(false);
    if (refusalHolds(true)) turn.reload();
    // refusalHolds reads the workspace of this render; it is not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [continuing, canvasClosed, turn]);
  const continueWithout = () => {
    if (!refusalHolds() || !gate.allow()) return;
    // A close the person cancels (unsaved edits) or that is refused sends nothing.
    if (ws?.openCanvas(null)) setContinuing(true);
  };
  // The transport already classified this failure as an unconfirmed submission of THIS original (the SDK only
  // hands the UI the error's message string), so the raw browser text is not shown; UnconfirmedNotice speaks.
  if (checkAgain && marked && original?.id === marked.messageId) return null;
  return (
    <MessagePrimitive.Error>
      {/* A turn that could not finish is recoverable: a calm note in the reply's place, the failure on the glyph. */}
      <ErrorPrimitive.Root className="aui-message-error-root border-border/70 bg-muted/40 text-foreground mt-2 flex items-start gap-2.5 rounded-xl border p-3 text-sm">
        <AlertCircleIcon aria-hidden className="text-destructive mt-0.5 size-4 shrink-0" />
        <div className="min-w-0 flex-1">
          <ErrorPrimitive.Message className="aui-message-error-message block text-pretty">
            {localizeWorkspaceError(chatErrorMessage(error, errorCopy), locale)}
          </ErrorPrimitive.Message>
          {/* A turn that could not reach its App: a gone preview can be left behind; a saved App opens again from Apps. */}
          {ws && editing && (
            <div className="mt-2.5 flex flex-wrap gap-2">
              {recoverable && (
                <button
                  type="button"
                  className="bg-foreground text-background hover:bg-foreground/90 rounded-full px-3 py-1 text-sm transition-colors ease-in disabled:opacity-60"
                  disabled={continuing}
                  onClick={continueWithout}
                >
                  {errorCopy.continueWithoutPreview}
                </button>
              )}
              <button
                type="button"
                className="bg-background hover:bg-muted/80 text-foreground rounded-full border px-3 py-1 text-sm transition-colors ease-in"
                onClick={() => ws.navigate('apps')}
              >
                {errorCopy.openApps}
              </button>
            </div>
          )}
        </div>
      </ErrorPrimitive.Root>
    </MessagePrimitive.Error>
  );
};

// Without a visible notice a length-capped turn reads as a broken chat: the
// text stops mid-sentence, or the message is completely empty when the cap
// landed inside the reasoning block. The continue affordance is a plain
// suggestion send so the model resumes in-thread; it is offered only on the
// last message, where continuing still makes sense.
const TruncationNotice: FC = () => {
  const copy = useChatCopy();
  const { needsAuth, requestAuth } = useQuestionGate();
  // The runtime's ThreadMessage metadata is whitelist-normalized
  // (fromThreadMessageLike drops unknown keys), so the server's messageMetadata
  // survives only on the original AI SDK message — reach it through
  // getExternalStoreMessages, with the ThreadMessage metadata as fallback for
  // runtimes that surface it directly. Known limit: the signed-in server
  // mirror stores only {id, role, parts}, so a truncated thread hydrated on
  // another device shows no notice — graceful degradation, not an error.
  const truncated = useAuiState((s) => {
    if (s.message.role !== 'assistant' || s.message.status?.type === 'running') return false;
    const external = getExternalStoreMessages(s.message)[0] as { metadata?: unknown } | undefined;
    return truncatedByLength(external?.metadata ?? s.message.metadata);
  });
  const { readOnly = false } = useContext(ThreadComponentsContext);
  const showContinue = useAuiState((s) => !s.thread.isRunning && s.message.isLast) && !readOnly;
  if (!truncated) return null;
  return (
    <div
      data-slot="aui_truncation-notice"
      className="border-border/70 bg-muted/40 text-muted-foreground mt-2 flex flex-wrap items-center gap-2 rounded-xl border p-3 text-sm"
    >
      <span>{copy.answerTruncated}</span>
      {showContinue && (
        <ThreadPrimitive.Suggestion
          className="bg-background hover:bg-muted/80 text-foreground shrink-0 rounded-full border px-3 py-1 text-sm transition-colors ease-in"
          prompt={copy.continueAnswer}
          {...(needsAuth ? { onClick: requestAuth } : { send: true })}
        >
          {copy.continueAnswer}
        </ThreadPrimitive.Suggestion>
      )}
    </div>
  );
};

// The ToolGroupNote slot for one "N tool calls" row, given its calls' ids.
const ToolGroupNoteSlot: FC<{ group: ThreadGroupPart }> = ({ group }) => {
  const { ToolGroupNote } = useContext(ThreadComponentsContext);
  const messageParts = useAuiState((s) => s.message.parts);
  if (!ToolGroupNote) return null;
  const toolCallIds = group.indices.flatMap((index) => {
    const part = messageParts[index];
    return part?.type === 'tool-call' ? [part.toolCallId] : [];
  });
  return <ToolGroupNote toolCallIds={toolCallIds} />;
};

/**
 * The assistant message's parts, grouped. Exported so a contract test can mount
 * the real grouping (which tool collapses into the "N tool calls" row and which
 * renders on its own) without the whole thread chrome.
 */
export const AssistantMessageParts: FC = () => {
  const copy = useChatCopy();
  const freshAction = useAuiState((state) => state.thread.isRunning && state.message.isLast);
  const messageId = useAuiState((state) => state.message.id);
  const messageParts = useAuiState((s) => s.message.parts);
  const [showAllSmallActions, setShowAllSmallActions] = useState(false);
  const workspace = useOptionalWorkspace();
  const company = useCompany();
  const actionScope = pendingActionScope(
    workspace?.user?.id,
    company.data?.company?.id,
    workspace?.threadId,
  );
  const pendingActions = useSyncExternalStore(
    pendingActionStore.subscribe,
    () => pendingActionStore.snapshot(actionScope),
    () => [],
  );
  const pendingInlineIds = new Set(
    pendingActions
      .filter(({ action }) => actionPresentation(action) === 'inline')
      .map(({ action }) => action.id),
  );
  // Connected calls staged in this message render together as one plan card (C17), on the
  // first of them; each keeps its own action and grant.
  const connectorItems = messageParts.flatMap((part) =>
    part.type === 'tool-call' &&
    CONNECTOR_ACTION_TOOLS.has(part.toolName) &&
    isActionPointer(part.result) &&
    part.result.status === 'confirmation_required'
      ? [{ pointer: part.result, origin: { messageId, toolCallId: part.toolCallId } }]
      : [],
  );
  const connectorIds = new Set(connectorItems.map((item) => item.pointer.action.id));
  const connectorGroup = connectorItems.filter(
    (item, index) =>
      connectorItems.findIndex((other) => other.pointer.action.id === item.pointer.action.id) ===
      index,
  );
  const smallActionCount = messageParts.filter(
    (part) =>
      part.type === 'tool-call' &&
      !RENDER_TOOL_NAMES.has(part.toolName) &&
      isActionPointer(part.result) &&
      !connectorIds.has(part.result.action.id) &&
      pendingInlineIds.has(part.result.action.id),
  ).length;
  const allToolIndices = messageParts.flatMap((part, index) =>
    part.type === 'tool-call' && !STANDALONE_TOOL_NAMES.has(part.toolName) ? [index] : [],
  );
  const { ToolFallback: ToolFallbackComponent = ToolFallback } =
    useContext(ThreadComponentsContext);
  // An App turn (the App kit's tools) reads as friendly step groups between the narration; every other turn keeps
  // today's single activity line.
  const buildTurn = isBuildMessage(messageParts);
  // Messages read mid-work split the steps: each one shows where Poneva read it,
  // with the steps it took next on their own line below it.
  const toolSegments = splitAtSteers(messageParts, allToolIndices);
  const firstSegment = toolSegments[0];
  const steerIds = messageParts.flatMap((part) => {
    const read = steerReadPart(part);
    return read ? [read.id] : [];
  });
  return (
    <>
      {!buildTurn && <MessageActivity toolIndices={allToolIndices} />}
      {!buildTurn &&
        (firstSegment.length > 0 ||
          (allToolIndices.length === 0 &&
            messageParts.some((part) => part.type === 'reasoning'))) && (
          <ToolActivity
            group={{ indices: firstSegment } as unknown as ThreadGroupPart}
            reasoningOnly={allToolIndices.length === 0}
            closed={steerIds.length > 0}
            sourceNote={
              <ToolGroupNoteSlot
                group={{ indices: allToolIndices } as unknown as ThreadGroupPart}
              />
            }
          />
        )}
      {smallActionCount > 1 && (
        <p className="pc-small-action-heading" data-slot="small-action-heading">
          {smallActionCount} {copy.toolNeedsApproval}
        </p>
      )}
      <MessagePrimitive.GroupedParts groupBy={groupAssistantParts}>
        {({ part, children }) => {
          switch (part.type) {
            case 'group-chainOfThought':
              return <div data-slot="aui_chain-of-thought">{children}</div>;
            case 'group-tool':
              return buildTurn ? <BuildStepGroup indices={part.indices} /> : null;
            case 'group-reasoning':
              // Reasoning is represented by the single activity line and its
              // trace step. Rendering the group here creates a second inline
              // widget for the same work.
              return null;
            case 'text':
              return <MarkdownText />;
            case 'reasoning':
              return <Reasoning {...part} />;
            case 'tool-call':
              if (RENDER_TOOL_NAMES.has(part.toolName)) {
                // A draft card is an action: one card per action in a message, and a call
                // that found it already pending points back at it, like other actions.
                if (isActionPointer(part.result)) {
                  const pointer = part.result;
                  const first = messageParts.find(
                    (candidate) =>
                      candidate.type === 'tool-call' &&
                      isActionPointer(candidate.result) &&
                      candidate.result.action.id === pointer.action.id,
                  );
                  if (first?.type === 'tool-call' && first.toolCallId !== part.toolCallId)
                    return null;
                  if (pointer.status === 'confirmation_already_pending')
                    return <ActionConfirmationReference pointer={pointer} />;
                }
                return <RenderToolPart {...part} messageId={messageId} fresh={freshAction} />;
              }
              if (isLegacyVoiceReceipt(part.result)) return <LegacyVoiceReceipt />;
              if (isActionPointer(part.result)) {
                const pointer = part.result;
                const firstPointerIndex = messageParts.findIndex(
                  (candidate) =>
                    candidate.type === 'tool-call' &&
                    isActionPointer(candidate.result) &&
                    candidate.result.action.id === pointer.action.id,
                );
                const currentPointerIndex = messageParts.findIndex(
                  (candidate) =>
                    candidate.type === 'tool-call' && candidate.toolCallId === part.toolCallId,
                );
                if (currentPointerIndex !== firstPointerIndex) return null;
                if (connectorIds.has(pointer.action.id)) {
                  return connectorGroup[0]?.pointer.action.id === pointer.action.id ? (
                    <ConnectorApprovalGroup
                      key={`${actionScope}:${messageId}`}
                      items={connectorGroup}
                      fresh={freshAction}
                      hasFollowingReply={messageParts
                        .slice(currentPointerIndex + 1)
                        .some((following) => following.type === 'text' && following.text.trim())}
                    />
                  ) : null;
                }
                if (pointer.status === 'confirmation_already_pending')
                  return <ActionConfirmationReference pointer={pointer} />;
                const isPendingSmallAction = pendingInlineIds.has(part.result.action.id);
                const smallActionOrdinal = isPendingSmallAction
                  ? messageParts
                      .slice(0, messageParts.indexOf(part) + 1)
                      .filter(
                        (candidate) =>
                          candidate.type === 'tool-call' &&
                          !RENDER_TOOL_NAMES.has(candidate.toolName) &&
                          isActionPointer(candidate.result) &&
                          !connectorIds.has(candidate.result.action.id) &&
                          pendingInlineIds.has(candidate.result.action.id),
                      ).length
                  : 0;
                if (!showAllSmallActions && isPendingSmallAction && smallActionOrdinal > 3) {
                  // Do not mount the hidden cards. ActionConfirmationCard registers
                  // its action subscription on mount; keeping cards under `hidden`
                  // caused four pending rows to keep re-registering while the group
                  // was collapsed. The fourth slot is a stable disclosure control;
                  // the remaining cards enter the tree only after it is opened.
                  if (smallActionOrdinal !== 4) return null;
                  return (
                    <button
                      type="button"
                      className="pc-small-action-show-more"
                      onClick={() => setShowAllSmallActions(true)}
                    >
                      {copy.more} {smallActionCount - 3}
                    </button>
                  );
                }
                return (
                  <ActionConfirmationCard
                    pointer={part.result}
                    fresh={freshAction}
                    origin={{ messageId, toolCallId: part.toolCallId }}
                  />
                );
              }
              return part.toolUI ?? <ToolFallbackComponent {...part} />;
            case 'data': {
              const read = steerReadPart(part);
              if (read) {
                const ordinal = steerIds.indexOf(read.id);
                return (
                  <SteerRead
                    text={read.text}
                    after={buildTurn ? [] : (toolSegments[ordinal + 1] ?? [])}
                    closed={ordinal + 1 < steerIds.length}
                  />
                );
              }
              // Unread messages wait at the end of the chat (WaitingMessages), not here.
              if (steerUnreadPart(part)) return null;
              return part.dataRendererUI;
            }
            case 'indicator':
              return <AssistantMessageThinkingIndicator />;
            default:
              return null;
          }
        }}
      </MessagePrimitive.GroupedParts>
      <CompactionStatus />
    </>
  );
};

const AssistantMessage: FC = () => {
  // reserves space for action bar and compensates with `-mb` for consistent msg spacing
  // keeps hovered action bar from shifting layout (autohide doesn't support absolute positioning well)
  // for pt-[n] use -mb-[n + 6] & min-h-[n + 6] to preserve compensation
  const ACTION_BAR_PT = 'pt-1.5';
  // The action-bar buttons are real 44px touch targets on mobile. Reserve
  // enough footer space that they cannot overlap the following message.
  const ACTION_BAR_HEIGHT = `-mb-5 min-h-[50px] ${ACTION_BAR_PT}`;

  return (
    <MessagePrimitive.Root
      data-slot="aui_assistant-message-root"
      data-role="assistant"
      className="fade-in slide-in-from-bottom-1 animate-in relative duration-150"
    >
      <div
        data-slot="aui_assistant-message-content"
        dir="auto"
        // [contain-intrinsic-size:auto_24px] fixes issue #4104, don't change without checking for regressions
        className="text-foreground px-2 leading-relaxed wrap-break-word [contain-intrinsic-size:auto_24px] [content-visibility:auto]"
      >
        <AssistantMessageParts />
        <InterruptedReply />
        <AnswerProgressNotice />
        <TruncationNotice />
        <MessageError />
        <FollowedSkills />
      </div>

      <div
        data-slot="aui_assistant-message-footer"
        className={cn('ms-2 flex min-w-0 flex-wrap items-center gap-0.5', ACTION_BAR_HEIGHT)}
      >
        <BranchPicker />
        <AssistantActionBar />
        <AnswerModel />
      </div>
    </MessagePrimitive.Root>
  );
};

const AssistantActionBar: FC = () => {
  const gate = useQuestionGate();
  const copy = useChatCopy();
  const { readOnly = false, MessageMenuItems } = useContext(ThreadComponentsContext);
  const actionIconClass = touchControlVariants({ density: 'answerIcon', surface: 'quiet' });
  return (
    <ActionBarPrimitive.Root
      hideWhenRunning
      autohide="not-last"
      className="aui-assistant-action-bar-root text-muted-foreground animate-in fade-in flex items-center gap-0.5 duration-200"
    >
      <ActionBarPrimitive.Copy asChild>
        <TooltipIconButton tooltip={copy.copy} className={cn(actionIconClass, 'rounded-full')}>
          <AuiIf condition={(s) => s.message.isCopied}>
            <CheckIcon className="animate-in zoom-in-50 fade-in duration-200 ease-out" />
          </AuiIf>
          <AuiIf condition={(s) => !s.message.isCopied}>
            <CopyIcon className="animate-in zoom-in-75 fade-in duration-150" />
          </AuiIf>
        </TooltipIconButton>
      </ActionBarPrimitive.Copy>
      <ReadAloudAction />
      <ActionBarMorePrimitive.Root>
        <ActionBarMorePrimitive.Trigger asChild>
          <TooltipIconButton
            tooltip={copy.more}
            className={cn(actionIconClass, 'rounded-full data-[state=open]:bg-accent')}
          >
            <MoreHorizontalIcon />
          </TooltipIconButton>
        </ActionBarMorePrimitive.Trigger>
        <ActionBarMorePrimitive.Content
          side="bottom"
          align="start"
          sideOffset={6}
          className="aui-action-bar-more-content bg-popover/95 text-popover-foreground data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=closed]:animate-out data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 min-w-48 overflow-hidden rounded-xl border p-1.5 shadow-lg backdrop-blur-sm"
        >
          {!readOnly && (
            <ActionBarPrimitive.Reload
              asChild
              onClick={(event) => {
                if (!gate.allow()) event.preventDefault();
              }}
            >
              <ActionBarMorePrimitive.Item className="aui-action-bar-more-item hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground data-disabled:text-muted-foreground flex min-h-11 cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm outline-none select-none data-disabled:cursor-not-allowed data-disabled:opacity-40">
                <RefreshCwIcon className="size-4" />
                {copy.tryAgain}
              </ActionBarMorePrimitive.Item>
            </ActionBarPrimitive.Reload>
          )}
          {MessageMenuItems && <MessageMenuItems />}
          <ActionBarPrimitive.ExportMarkdown asChild>
            <ActionBarMorePrimitive.Item className="aui-action-bar-more-item hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground flex min-h-11 cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm outline-none select-none">
              <DownloadIcon className="size-4" />
              {copy.exportMarkdown}
            </ActionBarMorePrimitive.Item>
          </ActionBarPrimitive.ExportMarkdown>
        </ActionBarMorePrimitive.Content>
      </ActionBarMorePrimitive.Root>
    </ActionBarPrimitive.Root>
  );
};

const UserMessage: FC = () => {
  const { UserMessageHeader, UserMessageWrapper } = useContext(ThreadComponentsContext);
  const message = (
    <MessagePrimitive.Root
      data-slot="aui_user-message-root"
      className="fade-in slide-in-from-bottom-1 animate-in grid auto-rows-auto grid-cols-[minmax(72px,1fr)_auto] content-start gap-y-2 px-2 duration-150 [contain-intrinsic-size:auto_60px] [content-visibility:auto] [&:where(>*)]:col-start-2"
      data-role="user"
    >
      <UserMessageAttachments />

      <div className="aui-user-message-content-wrapper relative col-start-2 min-w-0">
        {UserMessageHeader && <UserMessageHeader />}
        <div
          dir="auto"
          className="aui-user-message-content peer bg-muted text-foreground rounded-2xl rounded-ee-md px-4 py-2.5 wrap-break-word empty:hidden"
        >
          <MessagePrimitive.Parts />
        </div>
        <div className="aui-user-action-bar-wrapper absolute start-0 top-1/2 -translate-x-full -translate-y-1/2 pe-2 peer-empty:hidden rtl:translate-x-full">
          <UserActionBar />
        </div>
      </div>

      <BranchPicker
        data-slot="aui_user-branch-picker"
        className="col-span-full col-start-1 row-start-3 -me-1 justify-end"
      />
    </MessagePrimitive.Root>
  );
  return UserMessageWrapper ? <UserMessageWrapper>{message}</UserMessageWrapper> : message;
};

const UserActionBar: FC = () => {
  const copy = useChatCopy();
  const actionIconClass = touchControlVariants({ density: 'answerIcon', surface: 'quiet' });
  const { readOnly = false } = useContext(ThreadComponentsContext);
  if (readOnly) return null;
  return (
    <ActionBarPrimitive.Root
      autohide="not-last"
      className="aui-user-action-bar-root flex flex-col items-end"
    >
      <ActionBarPrimitive.Edit asChild>
        <TooltipIconButton
          tooltip={copy.edit}
          className={cn(actionIconClass, 'aui-user-action-edit rounded-full')}
        >
          <PencilIcon />
        </TooltipIconButton>
      </ActionBarPrimitive.Edit>
    </ActionBarPrimitive.Root>
  );
};

const EditComposer: FC = () => {
  const repair = useMessageRepair();
  useBillingEditDraftGuard();
  const gate = useQuestionGate();
  const copy = useChatCopy();
  if (repair) return <MessageRepairComposer />;
  return (
    <MessagePrimitive.Root data-slot="aui_edit-composer-wrapper" className="flex flex-col px-2">
      <ComposerPrimitive.Root
        onSubmit={(event) => {
          if (!gate.allow()) event.preventDefault();
        }}
        className="aui-edit-composer-root border-border/70 dark:border-muted-foreground/15 ms-auto flex w-full max-w-[85%] flex-col rounded-(--composer-radius) border bg-(--composer-bg) shadow-(--shadow-soft)"
      >
        <ComposerPrimitive.Input
          dir="auto"
          className="aui-edit-composer-input text-foreground min-h-14 w-full resize-none bg-transparent px-4 pt-3 pb-1 text-base outline-none"
          autoFocus={shouldAutoFocusComposer()}
        />
        <div className="aui-edit-composer-footer mx-2.5 mb-2.5 flex items-center gap-1.5 self-end">
          <ComposerPrimitive.Cancel asChild>
            <Button variant="ghost" size="sm" className="h-8 rounded-full px-3.5">
              {copy.cancel}
            </Button>
          </ComposerPrimitive.Cancel>
          <ComposerPrimitive.Send
            asChild
            onClick={(event) => {
              if (!gate.allow()) event.preventDefault();
            }}
          >
            <Button size="sm" className="h-8 rounded-full px-3.5">
              {copy.update}
            </Button>
          </ComposerPrimitive.Send>
        </div>
      </ComposerPrimitive.Root>
    </MessagePrimitive.Root>
  );
};

const BranchPicker: FC<BranchPickerPrimitive.Root.Props> = ({ className, ...rest }) => {
  const copy = useChatCopy();
  const actionIconClass = touchControlVariants({ density: 'answerIcon', surface: 'quiet' });
  return (
    <BranchPickerPrimitive.Root
      hideWhenSingleBranch
      className={cn(
        'aui-branch-picker-root text-muted-foreground -ms-2 me-2 inline-flex items-center text-xs',
        className,
      )}
      {...rest}
    >
      <BranchPickerPrimitive.Previous asChild>
        <TooltipIconButton tooltip={copy.previous} className={cn(actionIconClass, 'rounded-full')}>
          <ChevronLeftIcon />
        </TooltipIconButton>
      </BranchPickerPrimitive.Previous>
      <span className="aui-branch-picker-state font-medium">
        <BranchPickerPrimitive.Number /> / <BranchPickerPrimitive.Count />
      </span>
      <BranchPickerPrimitive.Next asChild>
        <TooltipIconButton tooltip={copy.next} className={cn(actionIconClass, 'rounded-full')}>
          <ChevronRightIcon />
        </TooltipIconButton>
      </BranchPickerPrimitive.Next>
    </BranchPickerPrimitive.Root>
  );
};
